import React from 'react';

import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { Cell } from 'react-table';

import { useAppSelector } from 'hooks';
import { Table } from 'components/UI';
import { DeleteIcon } from 'assets/icons';
import { getOperator } from 'constants/constants';
import {
  SmartEventBaselineComponent,
  SmartEventCondition,
  SmartEventConditionType,
  SmartEventDateTarget
} from 'constants/enums';
import { getPatientStatusOptions } from 'utils/UIStoreFunctions';
import { getMeasurementsOptions } from 'utils/Measurements';
import { formatTimeTo12Hour } from 'utils/Time';
import { getQuestionnaires } from 'store/groups/reducer';

interface ISmartEventConditionsTableProps {
  conditions: ISmartEventConditionForm[];
  setConditions?: any;
}

const SmartEventConditionsTable = ({
  conditions,
  setConditions
}: ISmartEventConditionsTableProps) => {
  const intl = useIntl();
  const measurementClasses = useAppSelector((state) => state.settings.citc?.measurementClasses);
  const measurementClassOptions = measurementClasses
    ? getMeasurementsOptions(measurementClasses)
    : {};
  const groupQuestionnaires = useAppSelector(getQuestionnaires);
  const locale = useAppSelector((state) => state.intl.locale);

  const translateTitleFromScore = (scoreTitle: string) => {
    const questionnaire = groupQuestionnaires.find(
      (questionnaire) => questionnaire.score?.title === scoreTitle
    );
    return intl.formatMessage({
      id: questionnaire?.title || '-'
    });
  };

  const toReadableTimeUnit = (character?: string) => {
    switch (character) {
      case 'd':
        return intl.formatMessage({ id: 'daysKey' }).toLowerCase();
      case 'h':
        return intl.formatMessage({ id: 'hoursKey' }).toLowerCase();
      default:
        return character;
    }
  };

  const getConditionClassLabel = (condition: ISmartEventConditionForm) => {
    switch (condition.conditionType) {
      case SmartEventConditionType.DATE:
        switch (condition.dateTarget) {
          case SmartEventDateTarget.PREGNANCY_WEEKS:
            return intl.formatMessage({ id: 'pregnancyWeeksKey' });
          case SmartEventDateTarget.ONBOARDING_DAYS:
            return intl.formatMessage({ id: 'onboardingDaysKey' });
          case SmartEventDateTarget.TIME_SINCE_REFERRED:
            return intl.formatMessage({ id: 'timeSinceReferredKey' });
          case SmartEventDateTarget.TIME_SINCE_DEVICE_ORDERED:
            return intl.formatMessage({ id: 'timeSinceDeviceOrderedKey' });
          case SmartEventDateTarget.TIME_SINCE_DEVICE_SHIPPED:
            return intl.formatMessage({ id: 'timeSinceDeviceShippedKey' });
          case SmartEventDateTarget.TIME_SINCE_FIRST_READING_TRANSMITTED:
            return intl.formatMessage({ id: 'timeSinceFirstReadingTransmittedKey' });
          case SmartEventDateTarget.TIME_SINCE_ENROLLED:
            return intl.formatMessage({ id: 'timeSinceEnrolledKey' });
          case SmartEventDateTarget.TIME_SINCE_PAUSED:
            return intl.formatMessage({ id: 'timeSincePausedKey' });
          default:
            return intl.formatMessage({ id: 'timeSinceToBeRemovedKey' });
        }
      case SmartEventConditionType.COMPLIANCE:
        return intl.formatMessage({ id: 'noMeasurementsSinceKey' });
      case SmartEventConditionType.PROFILE:
        return intl.formatMessage({ id: 'patientStatusKey' });
      default:
        return intl.formatMessage({
          id: condition.measurementClassCondition
            ? condition.measurementClassCondition
            : translateTitleFromScore(condition.questionnaireCondition)
        });
    }
  };

  const getBaselineConditionLabel = (condition: ISmartEventConditionForm) => {
    const directionKeys: { [key: string]: string } = {
      [SmartEventCondition.BASELINE_RISE]: 'smartEventBaselineRiseKey',
      [SmartEventCondition.BASELINE_DROP]: 'smartEventBaselineDropKey',
      [SmartEventCondition.BASELINE_ANY]: 'smartEventBaselineAnyKey'
    };
    const componentKeys: { [key: string]: string } = {
      [SmartEventBaselineComponent.SYSTOLIC]: 'smartEventBaselineSystolicKey',
      [SmartEventBaselineComponent.DIASTOLIC]: 'smartEventBaselineDiastolicKey',
      [SmartEventBaselineComponent.EITHER]: 'smartEventBaselineEitherComponentKey'
    };
    const direction = intl.formatMessage({ id: directionKeys[condition.condition] });
    return condition.baselineComponent
      ? `${direction} (${intl.formatMessage({ id: componentKeys[condition.baselineComponent] })})`
      : direction;
  };

  const conditionColumns: any[] = [
    {
      id: 'type',
      Header: intl.formatMessage({ id: 'typeKey' }),
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        switch (original.conditionType) {
          case SmartEventConditionType.MEASUREMENT:
            return intl.formatMessage({ id: 'measurementKey' });
          case SmartEventConditionType.QUESTIONNAIRE:
            return intl.formatMessage({ id: 'questionnaireScoreKey' });
          case SmartEventConditionType.DATE:
            return intl.formatMessage({ id: 'dateKey' });
          case SmartEventConditionType.COMPLIANCE:
            return intl.formatMessage({ id: 'complianceLabelKey' });
          case SmartEventConditionType.PROFILE:
            return intl.formatMessage({ id: 'profileTitle' });
          case SmartEventConditionType.BASELINE:
            return intl.formatMessage({ id: 'smartEventBaselineChangeKey' });
        }
      }
    },
    {
      id: 'class',
      Header: intl.formatMessage({ id: 'classKey' }),
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        const cellContent = getConditionClassLabel(original);
        return (
          <>
            {cellContent.length > 25 ? (
              <OverlayTrigger
                overlay={<Tooltip id="tooltip-condition-class">{cellContent}</Tooltip>}
              >
                <p className="clamp-text clamp-line-1 m-0">{cellContent}</p>
              </OverlayTrigger>
            ) : (
              <p className="m-0">{cellContent}</p>
            )}
          </>
        );
      }
    },
    {
      id: 'condition',
      Header: intl.formatMessage({ id: 'conditionKey' }),
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        switch (original.condition) {
          case SmartEventCondition.BASELINE_RISE:
          case SmartEventCondition.BASELINE_DROP:
          case SmartEventCondition.BASELINE_ANY:
            return getBaselineConditionLabel(original);
          case SmartEventCondition.REACH_DATE:
            return `${getOperator(
              parseInt(SmartEventCondition.GREATER_THAN_EQUAL)
            )} (${intl.formatMessage({ id: 'oneTimeKey' })}) ${intl.formatMessage({ id: 'evaluatedAtKey' })}: ${formatTimeTo12Hour(original.evalTime || '00:00', locale)}`;
          case SmartEventCondition.RECURRING_DATE:
            return `${getOperator(
              parseInt(SmartEventCondition.GREATER_THAN_EQUAL)
            )} (${intl.formatMessage({ id: 'recurrentKey' })}) ${intl.formatMessage({ id: 'evaluatedAtKey' })}: ${formatTimeTo12Hour(original.evalTime || '00:00', locale)}`;
          case SmartEventCondition.INCREASE:
            if (original.conditionType === SmartEventConditionType.MEASUREMENT) {
              return intl.formatMessage({ id: 'increaseMeasurementKey' });
            } else {
              return intl.formatMessage({ id: 'increaseScoreKey' });
            }
          case SmartEventCondition.DECREASE:
            if (original.conditionType === SmartEventConditionType.MEASUREMENT) {
              return intl.formatMessage({ id: 'decreaseMeasurementKey' });
            } else {
              return intl.formatMessage({ id: 'decreaseScoreKey' });
            }
          case SmartEventCondition.INCREASE_OVER_TIME:
            return intl.formatMessage(
              { id: 'increaseOverTimeLabelKey' },
              { value: original.timeWindowValue, unit: toReadableTimeUnit(original.timeWindowUnit) }
            );
          case SmartEventCondition.DECREASE_OVER_TIME:
            return intl.formatMessage(
              { id: 'decreaseOverTimeLabelKey' },
              { value: original.timeWindowValue, unit: toReadableTimeUnit(original.timeWindowUnit) }
            );
          case SmartEventCondition.INCREASE_FROM_REF:
            if (original.measurementClassCondition === 'weight') {
              return `${intl.formatMessage({ id: 'increaseSinceRefKey' })} ${intl.formatMessage({ id: 'dryWeightKey' })}`;
            } else {
              return `${intl.formatMessage({ id: 'increaseSinceRefKey' })}`;
            }
          case SmartEventCondition.DECREASE_FROM_REF:
            if (original.measurementClassCondition === 'weight') {
              return `${intl.formatMessage({ id: 'decreaseSinceRefKey' })} ${intl.formatMessage({ id: 'dryWeightKey' })}`;
            } else {
              return `${intl.formatMessage({ id: 'decreaseSinceRefKey' })}`;
            }
          default:
            return getOperator(parseInt(original.condition));
        }
      }
    },
    {
      id: 'value',
      Header: intl.formatMessage({ id: 'value' }),
      align: 'right',
      maxWidth: 75,
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        let text = original.value;
        if (
          original.conditionType === SmartEventConditionType.MEASUREMENT &&
          measurementClassOptions[original.measurementClassCondition]
        ) {
          text = intl.formatMessage({
            id: measurementClassOptions[original.measurementClassCondition][
              parseInt(original.value)
            ].title
          });
        } else if (original.conditionType === SmartEventConditionType.PROFILE) {
          text = getPatientStatusOptions().find((opt) => opt.value === original.value)?.label;
        }
        return (
          <OverlayTrigger overlay={<Tooltip id={`condition-${original.id}-value`}>{text}</Tooltip>}>
            <p className="white-space-nowrap text-overflow-ellipsis overflow-hidden">{text}</p>
          </OverlayTrigger>
        );
      }
    },
    {
      id: 'unit',
      Header: intl.formatMessage({ id: 'unit' }),
      maxWidth: 55,
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        let unitKey = original.valueUnit;
        if (
          original.conditionType === SmartEventConditionType.DATE ||
          original.conditionType === SmartEventConditionType.COMPLIANCE
        ) {
          if (original.dateTarget === SmartEventDateTarget.PREGNANCY_WEEKS) {
            unitKey = 'weeksKeys';
          } else {
            unitKey = 'daysKey';
          }
        }
        return unitKey ? intl.formatMessage({ id: unitKey, defaultMessage: unitKey }) : '';
      }
    }
  ];

  if (setConditions) {
    conditionColumns.push({
      id: 'delete',
      Header: ' ',
      Cell: ({ row: { original } }: Cell<ISmartEventConditionForm>) => {
        return (
          <Button
            variant="link"
            className="d-flex align-items-center"
            onClick={() => original.id && deleteCondition(original.id)}
          >
            <DeleteIcon className="icon pe-2" />
            <p className="m-0">{intl.formatMessage({ id: 'deleteKey' })}</p>
          </Button>
        );
      }
    });
  }

  const deleteCondition = (id: string) => {
    setConditions(conditions.filter((condition) => condition.id !== id));
  };

  return (
    <Table
      dataTestIdPrefix="conditions"
      columns={conditionColumns}
      data={conditions}
      noDataTxt={intl.formatMessage({ id: 'noDataKey' })}
      hideMargin
      autoHeight
    />
  );
};

export default SmartEventConditionsTable;
